<template>
    <div class="login-container">
        <h1 class="heading">Sign In Page</h1>
        <div v-if="!isAuthenticated">
            <div class="form-group">
                <label for="username" class="label">Username</label>
                <input
                    type="text"
                    id="username"
                    v-model="username"
                    placeholder="Enter username"
                    class="input-container"
                    :class="{ 'error': errorMessage }"
                />
            </div>

            <div class="form-group">
                <label for="password" class="label">Password</label>
                <input
                    type="password"
                    id="password"
                    v-model="password"
                    placeholder="Enter password"
                    class="input-container"
                    :class="{ 'error': errorMessage }"
                />
            </div>

            <div v-if="errorMessage" class="error-message">
                {{ errorMessage }}
            </div>

            <div v-if="successMessage" class="success-message">
                {{ successMessage }}
            </div>

            <TextButton
                :buttonText='"Login"'
                :dark="true"
                @click="handleLogin"
                class="login-button"
                type="submit"
                :disabled="isLoading"
            />
        </div>
        <div v-else>
            <p class="success-message">You are logged in!</p>
            <TextButton
                :buttonText='"Logout"'
                :dark="true"
                @click="handleLogout"
                class="login-button"
            />
        </div>
    </div>
</template>

<script>
import TextButton from "../components/TextButton.vue";
import ACTApiService from "../services/ACTApiService.js";

export default {
    components: {
        TextButton,
    },
    data() {
        return {
            username: "",
            password: "",
            errorMessage: "",
            successMessage: "",
            isAuthenticated: false,
            isLoading: false,
            userPool: null,
            cognitoUser: null,
            actApiService: new ACTApiService()
        };
    },
    async created() {
        if (!this.$route.path.startsWith('/scoring')) {
            const { CognitoUserPool } = await import('amazon-cognito-identity-js');
            
            const poolData = {
                UserPoolId: 'us-east-2_3novul0pN',
                ClientId: '51v76uda8o5l6niokhg852qnj5'
            };
            this.userPool = new CognitoUserPool(poolData);
            
            this.cognitoUser = this.userPool.getCurrentUser();
            if (this.cognitoUser) {
                this.cognitoUser.getSession((err, session) => {
                    if (err) {
                        console.error('Error getting session:', err);
                        return;
                    }
                    if (session.isValid()) {
                        this.checkUserRole(this.cognitoUser);
                    }
                });
            }
        }
    },
    methods: {
        async checkUserRole(cognitoUser) {
            cognitoUser.getUserAttributes((err, attributes) => {
                if (err) {
                    console.error('Error getting user attributes:', err);
                    this.errorMessage = "Error checking user role";
                    this.isLoading = false;
                    return;
                }

                console.log('All user attributes:', attributes);
                
                const featurePermissionsAttribute = attributes.find(attr => attr.Name === 'custom:featurePermissions');
                if (!featurePermissionsAttribute) {
                    console.log('No feature permissions attribute found');
                    this.errorMessage = "You do not have permission to access this site. If you think this is an error, contact us on slack or by email at bgc@northeastern.edu";
                    cognitoUser.signOut();
                    sessionStorage.removeItem("isLoggedIn");
                    this.isAuthenticated = false;
                    this.isLoading = false;
                    return;
                }

                console.log('Feature permissions attribute:', featurePermissionsAttribute);

                // Check if the user has the CanViewOwnScoringData permission (value 16384)
                const featurePermissions = parseInt(featurePermissionsAttribute.Value);
                const canViewOwnScoringData = 16384; // This is the bit flag for CanViewOwnScoringData (1 << 14)
                const hasPermission = (featurePermissions & canViewOwnScoringData) !== 0;

                console.log('Feature permissions value:', featurePermissions);
                console.log('CanViewOwnScoringData flag:', canViewOwnScoringData);
                console.log('Bitwise AND result:', featurePermissions & canViewOwnScoringData);
                console.log('Has permission:', hasPermission);
                
                /*
                if (!hasPermission) {
                    console.log('User does not have required permission');
                    this.errorMessage = "You do not have permission to access this site. If you think this is an error, contact us on slack or by email at bgc@northeastern.edu";
                    cognitoUser.signOut();
                    sessionStorage.removeItem("isLoggedIn");
                    this.isAuthenticated = false;
                    this.isLoading = false;
                    return;
                }
                    */
                
                console.log('User has required permission, allowing access');
                this.successMessage = "Login successful! Redirecting...";
                
                cognitoUser.getSession((err, session) => {
                    if (!err && session.isValid()) {
                        const token = session.getIdToken().getJwtToken();
                        this.actApiService.setAuthToken(token);
                    }
                });
                
                setTimeout(() => {
                    this.isAuthenticated = true;
                    sessionStorage.setItem("isLoggedIn", "true");
                    this.$router.push("/");
                }, 2000);
            });
        },
        async handleLogin() {
            if (!this.$route.path.startsWith('/scoring')) {
                this.isLoading = true;
                this.errorMessage = "";
                this.successMessage = "";
                
                // Development mode workaround
                if (process.env.NODE_ENV === 'development' && 
                    this.username === 'BGCDeveloper' && 
                    this.password === 'Password') {
                    this.successMessage = "Development mode login successful! Redirecting...";
                    setTimeout(() => {
                        this.isAuthenticated = true;
                        sessionStorage.setItem("isLoggedIn", "true");
                        this.$router.push("/");
                    }, 2000);
                    return;
                }
                
                const { AuthenticationDetails, CognitoUser } = await import('amazon-cognito-identity-js');
                
                const authenticationDetails = new AuthenticationDetails({
                    Username: this.username,
                    Password: this.password
                });

                const userData = {
                    Username: this.username,
                    Pool: this.userPool
                };

                this.cognitoUser = new CognitoUser(userData);

                this.cognitoUser.authenticateUser(authenticationDetails, {
                    onSuccess: () => {
                        this.checkUserRole(this.cognitoUser);
                    },
                    onFailure: (err) => {
                        this.errorMessage = err.message || "Authentication failed";
                        this.isLoading = false;
                    }
                });
            }
        },
        async handleLogout() {
            if (this.cognitoUser) {
                this.cognitoUser.signOut();
            }
            this.isAuthenticated = false;
            sessionStorage.removeItem("isLoggedIn");
            this.$router.push("/login");
        }
    },
};
</script>

<style scoped>
.login-container {
    max-width: 600px;
    height: 450px;
    margin: 0 auto;
    margin-top: 50px;
    padding: 40px;
    background-color: #F4F4F4;
    border: 1px solid #a0a0a0;
    border-radius: 6px;
    position: relative;
}

.heading {
    font-weight: normal;
    font-size: 30px;
    margin-bottom: 25px;
    color: #237091;
    text-align: center;
}

.login-button {
    padding: 12px;
    margin: 0 auto 0;
    text-align: center;
    padding: 0 30px;
}

.form-group {
    padding-bottom: 25px;
    text-align: left;
    max-width: 300px;
    margin: 0 auto;
}

.input-container {
    width: 100%;
    padding: 10px;
    font-size: 16px;
    border-radius: 8px;
    border: 2px solid #3986a8;
    box-sizing: border-box;
    transition: border-color 0.3s ease;
}

.input-container.error {
    border-color: #ff4444;
}

.label {
    display: block;
    font-size: 18px;
    color: #333;
    margin-bottom: 8px;
}

.error-message {
    color: #ff4444;
    margin-bottom: 25px;
    text-align: center;
    font-weight: bold;
}

.success-message {
    color: #00C851;
    margin-bottom: 25px;
    text-align: center;
    font-weight: bold;
}
</style> 